import React, { useEffect, useState } from 'react'
import { Button, Card, Col, Row, Spin, Table } from 'antd'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { lzDownloadBlobFile, wtApiCall } from '../../helpers/commons/ApiWrapper'
import { lzNotifyErrorMessageFromResult } from '../../helpers/commons/notifications'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import WidgetSelector from 'src/widgets/Selectors/WidgetSelector'
import { wtGetDateMonthYearFormat } from 'src/helpers/commons/moment'
import moment from 'moment'
import {
  getExcelDateExportConfig,
  useMasterDateTimeFormats,
} from 'src/helpers/commons/masterDateFormat'

const approvalStatusOptions = [
  { value: -1, label: 'All' },
  { value: 0, label: 'Unlocked' },
  { value: 1, label: 'Locked' },
]

export default function TalentPoolReport() {
  const { dateFormat, formatDateTime } = useMasterDateTimeFormats()
  const [isLoading, setIsLoading] = useState(false)
  const [data, setData] = useState([])
  const [branchId, setBranchId] = useState(0)
  const [branchName, setBranchName] = useState('')
  const [approvalStatus, setApprovalStatus] = useState(-1)
  const branches = wtGetFromLocalStorage('branches') || []

  const getReportData = () => {
    setIsLoading(true)
    wtApiCall(
      'reports/talent_pool_report',
      {
        branch_id: branchId,
        approval_status: approvalStatus,
      },
      'get',
      (response) => {
        setData(response.data)
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  useEffect(() => {
    getReportData()
  }, [])

  const getExportDateText = (value) => {
    if (!value) {
      return '-'
    }

    return `'${wtGetDateMonthYearFormat(value)}`
  }

  const escapeExcelValue = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

  const getRowStyle = (index) => {
    if (index % 2 === 0) {
      return 'background-color:#F8FBFF;'
    }

    return 'background-color:#FFFFFF;'
  }

  const buildExcelDateCell = (value, style) => {
    const exportConfig = getExcelDateExportConfig(value, dateFormat)

    if (exportConfig.type === 'empty') {
      return `<td style="${style}text-align:center;"></td>`
    }

    if (exportConfig.type === 'str') {
      return `<td style="${style}text-align:center;" x:str>${escapeExcelValue(
        exportConfig.value,
      )}</td>`
    }

    return `<td style="${style}text-align:center;mso-number-format:'${exportConfig.numberFormat}';" x:num>${exportConfig.value}</td>`
  }

  const buildExcelHtml = () => {
    const rows = data
      .map((item, index) => {
        const rowStyle = getRowStyle(index)

        return `
          <tr>
            <td style="${rowStyle}text-align:center;font-weight:700;color:#5B6573;">${escapeExcelValue(index + 1)}</td>
            <td style="${rowStyle}text-align:center;font-weight:700;color:#0B5ED7;">${escapeExcelValue(item.candidate_id || '-')}</td>
            <td style="${rowStyle}font-weight:700;color:#C2185B;">${escapeExcelValue(item.full_name || '-')}</td>
            <td style="${rowStyle}color:#000000;">${escapeExcelValue(item.father_husband_name || '-')}</td>
            <td style="${rowStyle}color:#37474F;">${escapeExcelValue(item.cnic || '-')}</td>
            ${buildExcelDateCell(item.date_of_birth, `${rowStyle}color:#7B1FA2;`)}
            <td style="${rowStyle}color:#000000;">${escapeExcelValue(item.gender || '-')}</td>
            <td style="${rowStyle}color:#455A64;">${escapeExcelValue(item.mobile_number || '-')}</td>
            <td style="${rowStyle}color:#455A64;">${escapeExcelValue(item.alternate_contact_number || '-')}</td>
            <td style="${rowStyle}color:#000000;">${escapeExcelValue(item.resident_address || '-')}</td>
            <td style="${rowStyle}color:#000000;">${escapeExcelValue(item.city || '-')}</td>
            <td style="${rowStyle}color:#000000;">${escapeExcelValue(item.reference || '-')}</td>
            <td style="${rowStyle}text-align:right;color:#0F172A;">${escapeExcelValue(item.total_experience || '-')}</td>
            <td style="${rowStyle}color:#000000;">${escapeExcelValue(item.educations || '-')}</td>
            <td style="${rowStyle}color:#000000;">${escapeExcelValue(item.specialization || '-')}</td>
            <td style="${rowStyle}color:#000000;">${escapeExcelValue(item.disability || '-')}</td>
            <td style="${rowStyle}color:#000000;">${escapeExcelValue(item.disability_details || '-')}</td>
            <td style="${rowStyle}color:#000000;">${escapeExcelValue(item.driving_license || '-')}</td>
            <td style="${rowStyle}color:#000000;">${escapeExcelValue(item.branch_name || '-')}</td>
            <td style="${rowStyle}font-weight:700;color:${item.approval_status_name === 'Locked' ? '#2E7D32' : '#AD1457'};">${escapeExcelValue(item.approval_status_name || '-')}</td>
            <td style="${rowStyle}color:#000000;">${escapeExcelValue(item.approved_by_name || '-')}</td>
            ${buildExcelDateCell(item.approved_at, `${rowStyle}color:#00838F;`)}
            ${buildExcelDateCell(item.created_at, `${rowStyle}color:#00838F;`)}
            ${buildExcelDateCell(item.updated_at, `${rowStyle}color:#00838F;`)}
          </tr>`
      })
      .join('')

    return `
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
        xmlns:x="urn:schemas-microsoft-com:office:excel"
        xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
          <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Talent Pool</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
          <style>
            body{font-family:Calibri,Arial,sans-serif;font-size:11pt;color:#000;}
            table{border-collapse:collapse;width:100%;}
            td,th{border:1px solid #cfd8e3;padding:6px 8px;vertical-align:middle;}
            .title{font-size:18pt;font-weight:700;text-align:center;border:none;padding:10px 0;}
            .meta{font-style:italic;text-align:center;border:none;color:#44546A;padding:4px 0 8px 0;}
            .header{background:#1F4E78;color:#fff;font-weight:700;text-align:center;}
          </style>
        </head>
        <body>
          <table>
            <tr><td class="title" colspan="24">Talent Pool Report</td></tr>
            <tr><td class="meta" colspan="24">Generated at: ${escapeExcelValue(formatDateTime(moment(), moment().format('YYYY-MM-DD HH:mm:ss')))}</td></tr>
            <tr>
              <th class="header">S#</th>
              <th class="header">Candidate ID</th>
              <th class="header">Full Name</th>
              <th class="header">Father/Husband Name</th>
              <th class="header">CNIC</th>
              <th class="header">Date of Birth</th>
              <th class="header">Gender</th>
              <th class="header">Mobile Number</th>
              <th class="header">Alternate Contact</th>
              <th class="header">Resident Address</th>
              <th class="header">City</th>
              <th class="header">Reference</th>
              <th class="header">Total Experience</th>
              <th class="header">Degree</th>
              <th class="header">Specialization</th>
              <th class="header">Disability (if any)</th>
              <th class="header">Disability Details</th>
              <th class="header">Driving License</th>
              <th class="header">Branch</th>
              <th class="header">Status</th>
              <th class="header">Approved By</th>
              <th class="header">Approved At</th>
              <th class="header">Created At</th>
              <th class="header">Updated At</th>
            </tr>
            ${rows}
          </table>
        </body>
      </html>
    `
  }

  const exportToExcel = async () => {
    setIsLoading(true)
    try {
      const excelBlob = new Blob([buildExcelHtml()], {
        type: 'application/vnd.ms-excel;charset=utf-8;',
      })
      lzDownloadBlobFile(
        excelBlob,
        `TalentPoolReport_${moment().format('YYYY_MM_DD_HH_mm_ss')}.xls`,
      )
    } finally {
      setIsLoading(false)
    }
  }

  const columns = [
    {
      key: 'sr',
      title: 'Sr.No',
      render: (_, __, index) => index + 1,
      fixed: 'left',
      width: 70,
    },
    { key: 'candidate_id', title: 'Candidate ID', dataIndex: 'candidate_id', width: 130, fixed: 'left' },
    { key: 'full_name', title: 'Full Name', dataIndex: 'full_name', width: 180 },
    { key: 'father_husband_name', title: 'Father/Husband Name', dataIndex: 'father_husband_name', width: 180 },
    { key: 'cnic', title: 'CNIC', dataIndex: 'cnic', width: 150 },
    {
      key: 'date_of_birth',
      title: 'Date of Birth',
      dataIndex: 'date_of_birth',
      width: 120,
      render: (value) => (value ? wtGetDateMonthYearFormat(value) : '-'),
    },
    { key: 'gender', title: 'Gender', dataIndex: 'gender', width: 100 },
    { key: 'mobile_number', title: 'Mobile Number', dataIndex: 'mobile_number', width: 150 },
    { key: 'alternate_contact_number', title: 'Alternate Contact', dataIndex: 'alternate_contact_number', width: 150 },
    { key: 'resident_address', title: 'Resident Address', dataIndex: 'resident_address', width: 220 },
    { key: 'city', title: 'City', dataIndex: 'city', width: 140 },
    { key: 'reference', title: 'Reference', dataIndex: 'reference', width: 160 },
    { key: 'total_experience', title: 'Total Experience', dataIndex: 'total_experience', width: 130 },
    { key: 'educations', title: 'Degree', dataIndex: 'educations', width: 220 },
    { key: 'specialization', title: 'Specialization', dataIndex: 'specialization', width: 180 },
    { key: 'disability', title: 'Disability (if any)', dataIndex: 'disability', width: 130 },
    { key: 'disability_details', title: 'Disability Details', dataIndex: 'disability_details', width: 220 },
    { key: 'driving_license', title: 'Driving License', dataIndex: 'driving_license', width: 140 },
    { key: 'branch_name', title: 'Branch', dataIndex: 'branch_name', width: 140 },
    { key: 'approval_status_name', title: 'Status', dataIndex: 'approval_status_name', width: 110 },
    { key: 'approved_by_name', title: 'Approved By', dataIndex: 'approved_by_name', width: 160 },
    {
      key: 'approved_at',
      title: 'Approved At',
      dataIndex: 'approved_at',
      width: 170,
      render: (value) => (value ? wtGetDateMonthYearFormat(value) : '-'),
    },
    {
      key: 'created_at',
      title: 'Created At',
      dataIndex: 'created_at',
      width: 170,
      render: (value) => (value ? wtGetDateMonthYearFormat(value) : '-'),
    },
    {
      key: 'updated_at',
      title: 'Updated At',
      dataIndex: 'updated_at',
      width: 170,
      render: (value) => (value ? wtGetDateMonthYearFormat(value) : '-'),
    },
  ]

  return (
    <div className="container-fluid" style={{ padding: '1em', backgroundColor: 'white', minHeight: '100vh' }}>
      <Card title={<span style={{ fontSize: '26px' }}>Talent Pool Report</span>}>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} md={6}>
            <label>Branch:</label>
            <WidgetBranchSelector
              value={branchId}
              displayValue={branchName}
              branches={branches}
              placeholder="Select Branch"
              onChange={(value) => {
                setBranchId(value?.value || 0)
                setBranchName(value?.label || '')
              }}
              showCloudIcon={false}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <label>Status:</label>
            <WidgetSelector
              options={approvalStatusOptions}
              placeHolder="Select Status"
              onChange={(value) => setApprovalStatus(value?.value ?? -1)}
              selectedValue={approvalStatus}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button
              style={{ marginTop: 30 }}
              type="primary"
              onClick={getReportData}
              disabled={isLoading}
            >
              Search
            </Button>
            <Button
              style={{ marginTop: 30, marginLeft: 8, backgroundColor: '#4BB543', color: '#fff' }}
              onClick={exportToExcel}
              disabled={isLoading || data.length === 0}
            >
              Export Excel
            </Button>
          </Col>
        </Row>
      </Card>

      <Card style={{ marginTop: 20 }}>
        <Spin spinning={isLoading}>
          <Table
            rowKey="id"
            dataSource={data}
            columns={columns}
            pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200] }}
            bordered
            scroll={{ x: 4200, y: 550 }}
            size="small"
          />
        </Spin>
      </Card>
    </div>
  )
}
